import firebase from 'firebase/app';

import 'firebase/remote-config';
import { DEFAULT_UI_CONFIG, UIConfig } from '../types/firebaseRemoteConfig';

// Lazy initialization of Remote Config
let remoteConfigInstance: firebase.remoteConfig.RemoteConfig | null = null;

function getRemoteConfig() {
  if (!remoteConfigInstance) {
    // Ensure Firebase is initialized
    if (!firebase.apps.length) {
      throw new Error(
        'Firebase is not initialized. Please initialize Firebase before using Remote Config.'
      );
    }

    remoteConfigInstance = firebase.remoteConfig();

    // Set config settings
    remoteConfigInstance.settings = {
      minimumFetchIntervalMillis: 300000, // 5 minutes in milliseconds
      fetchTimeoutMillis: 60000, // 60 seconds
    };

    // Set default values
    remoteConfigInstance.defaultConfig = {
      ui_config: JSON.stringify(DEFAULT_UI_CONFIG),
    };
  }

  return remoteConfigInstance;
}

/**
 * Type assertion to validate if an object matches the UIConfig interface
 * @param obj - The object to validate
 * @throws Error if the object is not a valid UIConfig
 */
function assertIsUIConfig(obj: unknown): asserts obj is UIConfig {
  if (typeof obj !== 'object' || obj === null) {
    throw new Error('Config is not an object');
  }

  const config = obj as Record<string, unknown>;

  if (
    !config.under_maintenance ||
    typeof config.under_maintenance !== 'object' ||
    config.under_maintenance === null
  ) {
    throw new Error('Config missing valid under_maintenance property');
  }

  const underMaintenance = config.under_maintenance as Record<string, unknown>;

  if (typeof underMaintenance.is_enabled !== 'boolean') {
    throw new Error('under_maintenance.is_enabled must be a boolean');
  }

  // Optional fields should be string, null, or undefined
  if (
    underMaintenance.title !== undefined &&
    underMaintenance.title !== null &&
    typeof underMaintenance.title !== 'string'
  ) {
    throw new Error('under_maintenance.title must be a string, null, or undefined');
  }

  if (
    underMaintenance.description !== undefined &&
    underMaintenance.description !== null &&
    typeof underMaintenance.description !== 'string'
  ) {
    throw new Error('under_maintenance.description must be a string, null, or undefined');
  }

  if (
    underMaintenance.image_url !== undefined &&
    underMaintenance.image_url !== null &&
    typeof underMaintenance.image_url !== 'string'
  ) {
    throw new Error('under_maintenance.image_url must be a string, null, or undefined');
  }
}

/**
 * Fetches and activates the remote config from Firebase
 * @param force - If true, bypasses the cache and forces a fresh fetch from Firebase
 * @returns Promise that resolves to true if new config was activated
 */
export async function fetchAndActivateRemoteConfig(force = false): Promise<boolean> {
  try {
    const remoteConfig = getRemoteConfig();

    if (force) {
      // Temporarily set minimum fetch interval to 0 to bypass cache
      const originalInterval = remoteConfig.settings.minimumFetchIntervalMillis;
      remoteConfig.settings.minimumFetchIntervalMillis = 0;

      // Fetch and activate
      const activated = await remoteConfig.fetchAndActivate();

      // Restore original interval
      remoteConfig.settings.minimumFetchIntervalMillis = originalInterval;

      return activated;
    }

    const activated = await remoteConfig.fetchAndActivate();
    return activated;
  } catch {
    return false;
  }
}

/**
 * Gets the UI config from Firebase Remote Config
 * @returns The UI configuration object
 */
export function getUIConfig(): UIConfig {
  try {
    const remoteConfig = getRemoteConfig();
    const configValue = remoteConfig.getValue('ui_config');
    const configString = configValue.asString();

    if (!configString) {
      return DEFAULT_UI_CONFIG;
    }

    const parsedConfig = JSON.parse(configString);

    try {
      assertIsUIConfig(parsedConfig);
    } catch {
      // eslint-disable-next-line no-console
      console.error('Invalid UI config:', parsedConfig);
      return DEFAULT_UI_CONFIG;
    }

    return parsedConfig;
  } catch {
    return DEFAULT_UI_CONFIG;
  }
}
